import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { Testimonials } from '../components/Testimonials';
import { HowItWorksImages } from '../components/HowItWorksImages';
import { HelpBubble } from '../components/HelpBubble';
import { PopularBunches } from '../components/PopularBunches';
import { FeaturedFloristsSection } from '../components/FeaturedFloristsSection';
import { supabase } from '@/integrations/supabase/client';
import type { FloristProfile } from '../components/FeaturedFlorists';
import { Helmet } from 'react-helmet-async';

// Define interface for popular products
interface PopularProduct {
  id: string;
  title: string;
  price: number;
  main_image_url?: string;
  store_id: string;
  florist_name?: string;
}

const Index = () => {
  const [featuredFloristsData, setFeaturedFloristsData] = useState<FloristProfile[]>([]);
  const [popularProducts, setPopularProducts] = useState<PopularProduct[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([fetchFeaturedFlorists(), fetchPopularProducts()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Fetch featured florists from Supabase
  const fetchFeaturedFlorists = async () => {
    try {
      const { data, error } = await supabase
        .from('store_profiles')
        .select('id, store_name, storefront_image_url, cover_image_url, suburb, state, featured, description')
        .eq('featured', true)
        .limit(6);

      if (error) throw error;
      
      const mappedData = (data || []).map(florist => ({
        id: florist.id,
        store_name: florist.store_name,
        about_text: florist.description || '',
        suburb: florist.suburb,
        state: florist.state,
        storefront_image_url: florist.storefront_image_url,
        banner_url: florist.cover_image_url,
        cover_image_url: florist.cover_image_url,
        delivery_settings: {},
      }));
      
      setFeaturedFloristsData(mappedData as FloristProfile[]);
    } catch (error) {
      console.error("Error fetching featured florists:", error);
      setFeaturedFloristsData([]);
    }
  };

  // Fetch popular products from Supabase
  const fetchPopularProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, 
          title, 
          price, 
          main_image_url,
          store_id,
          store_profiles(store_name)
        `)
        .eq('featured', true)
        .eq('active', true)
        .limit(8);

      if (error) throw error;

      console.log('Featured products from database:', data);
      console.log('Number of featured products:', data?.length || 0);

      // Format the products data
      const formattedProducts = (data || []).map(product => ({
        id: product.id,
        title: product.title,
        price: product.price,
        main_image_url: product.main_image_url,
        store_id: product.store_id,
        florist_name: product.store_profiles?.store_name
      }));

      console.log('Formatted featured products:', formattedProducts);
      setPopularProducts(formattedProducts);
    } catch (error) {
      console.error("Error fetching popular products:", error);
      setPopularProducts([]);
    }
  };

  const handleHeroSearch = (params: {
    location: string;
    date?: Date;
    budget?: [number, number];
    deliveryType: 'delivery' | 'pickup';
    coordinates?: { lat: number; lng: number };
  }) => {
    const queryParams = new URLSearchParams();
    if (params.location) queryParams.append('location', params.location);
    if (params.date) {
      const year = params.date.getFullYear();
      const month = (params.date.getMonth() + 1).toString().padStart(2, '0');
      const day = params.date.getDate().toString().padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      queryParams.append('date', dateString);
    }
    if (params.budget) {
      queryParams.append('minPrice', params.budget[0].toString());
      queryParams.append('maxPrice', params.budget[1].toString());
    }
    queryParams.append('deliveryType', params.deliveryType);
    if (params.coordinates) {
      queryParams.append('lat', params.coordinates.lat.toString());
      queryParams.append('lng', params.coordinates.lng.toString());
    }
    
    navigate(`/search?${queryParams.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      <Helmet>
        {/* Preload critical homepage images for maximum quality and speed */}
        <link rel="preload" as="image" href="/images/roses-hero.jpg" />
        <link rel="preload" as="image" href="/images/roses-hero5.jpg" />
        <link rel="preload" as="image" href="/images/roses-hero15.jpg" />
        <link rel="preload" as="image" href="/images/roses-hero14.jpg" />
      </Helmet>
      
      <Hero 
        onSearch={handleHeroSearch} 
        featuredFlorists={featuredFloristsData}
        popularProducts={popularProducts}
      />
      
      {/* Full-width promotional banner */}
      <section className="relative w-full h-[400px] overflow-hidden">
        <img 
          src="/images/roses-hero5.jpg"
          alt="Premium flowers from local florists"
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          style={{
            imageRendering: '-webkit-optimize-contrast',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'translateZ(0)'
          }}
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 h-full flex items-center justify-center px-4 md:px-6">
          <div className="text-center max-w-5xl">
            <h2 className="text-white text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-4 satoshi-font md:whitespace-nowrap">
              Shop flowers from trusted<br className="md:hidden" /> local florists
            </h2>
            <p className="text-white/90 text-lg md:text-xl satoshi-font mb-6">
              Same-day or scheduled delivery across Sydney
            </p>
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/40 px-6 py-2 rounded-full text-sm font-medium transition-all satoshi-font"
            >
              Browse Florists
            </button>
          </div>
        </div>
      </section>

      <HowItWorksImages />
      
      {/* What is MarketBloom Section */}
      <section className="w-full py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="max-w-3xl">
            <h2 className="text-2xl font-semibold tracking-tight mb-4 satoshi-font text-brand-secondary">
              What is MarketBloom?
            </h2>
            <p className="text-base text-neutral-700 mb-2 satoshi-font leading-relaxed">
              MarketBloom is a simple way to order flowers from real local florists — for pickup or delivery, on the day you choose.
            </p>
            <p className="text-base text-neutral-700 satoshi-font leading-relaxed">
              Enter your suburb and date, and we'll show you trusted florists nearby. Each one sets their own prices and prepares your order fresh — ready for pickup or delivery. Orders go straight to the shop. No middle steps — just real florists doing what they do best.
            </p>
          </div>
        </div>
      </section>
      
      {/* Move Popular Bunches here from Hero */}
      <PopularBunches products={popularProducts} />
      
      {/* Move Featured Florists here from Hero */}
      <FeaturedFloristsSection florists={featuredFloristsData} />
      
      <Testimonials />
      
      {/* Subtle help bubble for new users */}
      <HelpBubble />
    </div>
  );
};

export default Index; 